// src/utils/countCategory.js
// Подпись категории платежа (Count.group) на языке интерфейса. Категории в БД
// лежат по-русски: стандартные переводятся ключом groups_counts.*, остальные —
// категории, заведённые компанией, — показываются как есть.
//
// Раньше это был switch, скопированный в 17 компонентов, без ветки по
// умолчанию: неизвестная категория (своя категория компании, «Аренда с
// выкупом» — FT-172) давала пустую ячейку в кассе (приёмка 29.09).
const CATEGORY_KEYS = {
  "Оплата аренды": "groups_counts.rent",
  "Доплата за доставку": "groups_counts.delivery",
  "Доплата за приём": "groups_counts.checkout",
  "Доплата за топливо": "groups_counts.fuel",
  "Доплата за мойку": "groups_counts.clean",
  "Доплата за оборудование": "groups_counts.equipment",
  "Доплата за дополнительное время": "groups_counts.add_time",
  "Доплата за повреждения": "groups_counts.damages",
  "Доплата за штрафы": "groups_counts.fines",
  "Доплата за другое": "groups_counts.other",
  "Доплата за превышение пробега": "groups_counts.mileage",
  "Доплата за страховку": "groups_counts.insurance",
  "Доплата за доп водителей": "groups_counts.add_drivers",
  "Ремонт": "groups_counts.repair",
  "Обслуживание": "groups_counts.service",
  "Запчасти": "groups_counts.parts",
  "Страховка": "groups_counts.insurance_main",
  "Накладные расходы": "groups_counts.common_expences",
  "Транспортные расходы": "groups_counts.transport_expences",
  "Налоги": "groups_counts.tax",
  "Штрафы": "groups_counts.fines_main",
  "Аренда имущества": "groups_counts.rent_property",
  "Мойка автомобилей": "groups_counts.clean_main",
  "Топливо": "groups_counts.fuel_main",
  "Реклама": "groups_counts.ads",
  "Зарплата": "groups_counts.salary",
  "Залог": "groups_counts.deposit",
  "Внутренние переводы": "groups_counts.internal",
  "Продажа имущества": "groups_counts.sell_property",
  "Покупка имущества": "groups_counts.buy_property",
  "Прочие расходы": "groups_counts.other_expences",
  "Прочие доходы": "groups_counts.other_incomes",
  "Выплаты партнёрам": "groups_counts.partners_payments",
  "Сервисные сборы": "groups_counts.service_fees",
  "Пополнение баланса клиента": "groups_counts.top_up_client_balance",
  "Выплаты агентам": "groups_counts.agent_payments",
  "Аренда с выкупом": "groups_counts.rent_to_own",
};

export function translateCountCategory(t, category) {
  if (!category) return "";
  const key = CATEGORY_KEYS[category];
  return key ? t(key) : category;
}

export const COUNT_CATEGORY_KEYS = CATEGORY_KEYS;

// Значение категории (Count.group, по-русски) из того, что отдаёт v-combobox
// в диалогах правки платежа. Форм три, и все реально встречаются:
//   - объект { text, value } — категория, подставленная при открытии диалога;
//   - строка — выбранный пункт списка (v-select без return-object отдаёт
//     item-value) либо текст, набранный руками;
//   - null / "" — поле очищено.
// Раньше диалоги читали `.group` у { text, value } (всегда undefined) — ключ
// выпадал из JSON, и бэкенд молча оставлял старую категорию.
//
// `allowed` — допустимые значения (см. allowedCategoryValues). Набранный руками
// текст вне списка не должен становиться новой строкой group без
// counts_category_id: он отбрасывается, остаётся fallback (прежняя категория).
// Сам fallback допустим всегда: категория платежа может не входить в список
// диалога («Оплата аренды», «Аренда с выкупом»). Без `allowed` ограничения нет.
export function categoryValueOf(selection, fallback, allowed) {
  const raw = selection !== null && typeof selection === "object" ? selection.value : selection;
  if (typeof raw !== "string") return fallback;
  // trim() — только чтобы отсеять пустое: имена категорий с пробелами по краям
  // бэкенд допускает, сверяем и отправляем значение как есть.
  if (raw.trim() === "") return fallback;
  if (Array.isArray(allowed) && raw !== fallback && !allowed.includes(raw)) return fallback;
  return raw;
}

// Допустимые значения категории для диалога правки: пункты его списка
// ({ text, value }) плюс категории компании из store (company.counts_categories).
// `companyData` — getters.getCompany ({ company: { counts_categories } }), может отсутствовать.
export function allowedCategoryValues(items, companyData) {
  return [...(items || []), ...companyCountCategories(companyData)]
    .map((item) => item && item.value)
    .filter((v) => typeof v === "string");
}

// Категория, которую диалог правки отправляет в PATCH. Проведённому платежу
// (completed) категорию менять нельзя — шлём прежнюю, что бы ни стояло в поле.
export function categoryToSend(count, selection, allowed) {
  if (count.completed === true) return count.group;
  return categoryValueOf(selection, count.group, allowed);
}

// ---------------------------------------------------------------------------
// FT-192: дерево статей платежей — классы, ручной выбор, тип, подкатегории.
//
// Сервер отдаёт статьи с `code`, `kind`, `active` и `subcategories` (CTR-01).
// Старые данные и запасной список филиала без строк статей этих полей не имеют,
// поэтому класс и тип восстанавливаются по таблице ниже — она один в один
// повторяет `Analytics::MoneyGroups` и сид `CreateInitialCountsCategoriesJob`
// API. Статья, которой в таблице нет, заведена компанией: класса у неё нет (null).
// ---------------------------------------------------------------------------

// Название из сида (Count.group, по-русски) → код статьи (ключи MoneyGroups::KEYS).
const CATEGORY_CODES = {
  "Оплата аренды": "rental_cost",
  "Доплата за доставку": "delivery_payments",
  "Доплата за приём": "checkout_payments",
  "Доплата за топливо": "gas_payments",
  "Доплата за мойку": "clean_payments",
  "Доплата за оборудование": "equipment_payments",
  "Доплата за дополнительное время": "add_hours_payments",
  "Доплата за повреждения": "repair_payments",
  "Доплата за штрафы": "fines_payments",
  "Доплата за другое": "other_payments",
  "Доплата за превышение пробега": "add_mileage_payments",
  "Доплата за страховку": "insurance_payments",
  "Доплата за доп водителей": "drivers_payments",
  "Ремонт": "repair_expences",
  "Обслуживание": "service_expences",
  "Запчасти": "parts_expences",
  "Страховка": "insurance_expences",
  "Накладные расходы": "common_expences",
  "Транспортные расходы": "transport_expences",
  "Налоги": "tax_expences",
  "Штрафы": "fines_expences",
  "Аренда имущества": "office_expences",
  "Мойка автомобилей": "clean_expences",
  "Топливо": "gas",
  "Реклама": "ads_expences",
  "Зарплата": "salary",
  "Залог": "deposit",
  "Внутренние переводы": "internal_transfers",
  "Продажа имущества": "sell_property",
  "Аренда с выкупом": "rent_to_own",
  "Покупка имущества": "buy_property",
  "Прочие расходы": "other_expense",
  "Прочие доходы": "different_income",
  "Выплаты партнёрам": "partners_expences",
  "Сервисные сборы": "service_fees",
  "Пополнение баланса клиента": "client_balance_topup",
  "Выплаты агентам": "agents_expences",
};

// Классы-исключения; всё остальное в таблице — regular (MoneyGroups::KIND_EXCEPTIONS).
const KIND_EXCEPTIONS = {
  service_fees: "system",
  rent_to_own: "system",
  client_balance_topup: "system",
  deposit: "mode",
  internal_transfers: "mode",
  agents_expences: "service",
  rental_cost: "base",
};

// Доход (1) из сида; остальное — расход (0). Залог (2) и переводы (3) — режимы формы.
const INCOME_CODES = [
  "rental_cost",
  "delivery_payments",
  "checkout_payments",
  "gas_payments",
  "clean_payments",
  "equipment_payments",
  "add_hours_payments",
  "repair_payments",
  "fines_payments",
  "other_payments",
  "add_mileage_payments",
  "insurance_payments",
  "drivers_payments",
  "sell_property",
  "rent_to_own",
  "different_income",
  "client_balance_topup",
];
const MODE_OPERATION_TYPES = { deposit: 2, internal_transfers: 3 };

// Коды сида и доходные коды — для проверок «код известен» и «доход» без обхода таблицы на каждый вызов.
const KNOWN_CODES = new Set(Object.values(CATEGORY_CODES));
const INCOME_CODE_SET = new Set(INCOME_CODES);

export const COUNT_CATEGORY_CODES = CATEGORY_CODES;

// Код статьи: из ответа сервера либо по названию из сида; у статьи компании — null.
export function categoryCode(category) {
  if (!category) return null;
  if (typeof category.code === "string" && category.code !== "") return category.code;
  return CATEGORY_CODES[category.value] || null;
}

function kindByCode(code) {
  if (!code || !KNOWN_CODES.has(code)) return null;
  return KIND_EXCEPTIONS[code] || "regular";
}

function operationTypeByCode(code) {
  if (!code || !KNOWN_CODES.has(code)) return null;
  if (code in MODE_OPERATION_TYPES) return MODE_OPERATION_TYPES[code];
  return INCOME_CODE_SET.has(code) ? 1 : 0;
}

// Класс статьи: system | mode | service | base | regular | null (статья компании).
export function categoryKind(category) {
  if (!category) return null;
  if (typeof category.kind === "string" && category.kind !== "") return category.kind;
  return kindByCode(categoryCode(category));
}

// Статьи компании из store. `companyData` — getters.getCompany
// ({ company: { counts_categories } }), может отсутствовать. `fallback` — у филиала
// без строк статей вернуть запасной список (форма платежа, диалог правки, фильтр журнала).
export function companyCountCategories(companyData, { fallback = false } = {}) {
  const company = companyData && companyData.company;
  const list = company && Array.isArray(company.counts_categories) ? company.counts_categories : [];
  return fallback && list.length === 0 ? fallbackCategories() : list;
}

// Коды статей, скрытых компанией: getters.getMainCompany.hidden_count_category_codes.
// MainCompany ещё не загружена или поля нет — ничего не скрыто.
export function hiddenCategoryCodes(mainCompany) {
  const codes = mainCompany && mainCompany.hidden_count_category_codes;
  return Array.isArray(codes) ? codes : [];
}

// Статьи, которые можно выбрать руками в форме платежа.
//   - system (сервисные сборы, выкуп, пополнение баланса) — пишет только сервер;
//   - скрытые: `active === false` либо код в `hiddenCodes` (скрытие MainCompany).
// Режим оплаты долга (`debtMode`) — как было до FT-192: скрытие не действует,
// убираются только «Сервисные сборы» (код service_fees).
export function manualCategories(categories, { hiddenCodes, debtMode } = {}) {
  const list = Array.isArray(categories) ? categories : [];
  if (debtMode) return list.filter((category) => category && categoryCode(category) !== "service_fees");
  const hidden = Array.isArray(hiddenCodes) ? hiddenCodes : [];
  return list.filter((category) => {
    if (!category) return false;
    if (categoryKind(category) === "system") return false;
    if (category.active === false) return false;
    const code = categoryCode(category);
    return !(code && hidden.includes(code));
  });
}

// Расход ли статья: true — расход (operation_type 0), false — доход (1),
// null — режим формы («Залог», «Внутренние переводы») или тип не определить.
// У элемента запасного списка без operation_type тип берётся из таблицы сида.
export function isExpenseCategory(category) {
  if (!category) return null;
  let type = category.operation_type;
  if (type === undefined || type === null) type = operationTypeByCode(categoryCode(category));
  if (type === 0) return true;
  if (type === 1) return false;
  return null;
}

function bySubcategoryName(a, b) {
  return String(a.value).localeCompare(String(b.value), undefined, { sensitivity: "base" });
}

export function sameId(a, b) {
  return a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);
}

// Подкатегории статьи для выбора: активные по алфавиту. `includeInactiveId` —
// скрытая подкатегория, уже стоящая на платеже: её показываем, чтобы поле не
// выглядело пустым, но другим платежам она не предлагается (остальные скрытые
// не попадают в список).
// `includeInactive` — фильтр журнала: скрытые подкатегории тоже, ведь платежи на них остались.
export function subcategoriesFor(category, { includeInactiveId, includeInactive } = {}) {
  const list = category && Array.isArray(category.subcategories) ? category.subcategories : [];
  return list
    .filter((sub) => sub && (includeInactive === true || sub.active !== false || sameId(sub.id, includeInactiveId)))
    .slice()
    .sort(bySubcategoryName);
}

// Индекс подкатегорий дерева статей: String(id) → название. id подкатегории уникален
// во всём дереве; подкатегория без названия в индекс не попадает.
export function subcategoryIndex(categories) {
  const index = new Map();
  (Array.isArray(categories) ? categories : []).forEach((item) => {
    const subs = item && Array.isArray(item.subcategories) ? item.subcategories : [];
    subs.forEach((sub) => {
      if (!sub || sub.id === undefined || sub.id === null || !sub.value) return;
      const key = String(sub.id);
      if (!index.has(key)) index.set(key, sub.value);
    });
  });
  return index;
}

// «Статья · Подкатегория» для журнала. Подпись статьи — через translateCountCategory
// (`t` передаётся, когда нужен перевод; без него статья остаётся как в базе),
// подкатегория — текст клиента. Без подкатегории — только статья.
// `categories` — дерево статей либо готовый subcategoryIndex (журнал строит его один раз).
export function subcategoryLabel(count, categories, t) {
  if (!count) return "";
  const label = typeof t === "function" ? translateCountCategory(t, count.group) : count.group || "";
  if (count.counts_subcategory_id === undefined || count.counts_subcategory_id === null) return label;
  // Подпись статьи — по group (ASM-01), counts_category_id может разойтись с ней.
  // Подкатегория ищется по id среди подкатегорий всех статей дерева.
  const index = categories instanceof Map ? categories : subcategoryIndex(categories);
  const value = index.get(String(count.counts_subcategory_id));
  return value ? `${label} · ${value}` : label;
}

// Статьи диалога правки платежа: ручной выбор по правилам формы (manualCategories)
// плюс текущая статья платежа, даже если она системная, скрытая или снята с
// компании, — иначе поле выглядело бы пустым. `all` — полный список статей, из
// которого берётся текущая (с её подкатегориями); нет и там — строка без подкатегорий.
export function categoriesWithCurrent(manual, all, currentValue) {
  const list = Array.isArray(manual) ? manual : [];
  if (typeof currentValue !== "string" || currentValue === "") return list;
  if (list.some((category) => category && category.value === currentValue)) return list;
  const source = Array.isArray(all) ? all : [];
  const current = source.find((category) => category && category.value === currentValue);
  return [...list, current || { value: currentValue, subcategories: [] }];
}

// Значение фильтра журнала «Без подкатегории».
export const NO_SUBCATEGORY = "none";

// Платежи журнала с подкатегорией `subcategoryId` (NO_SUBCATEGORY — без неё).
// Пустой фильтр (null / "") ничего не отбрасывает.
export function filterBySubcategory(counts, subcategoryId) {
  const list = Array.isArray(counts) ? counts : [];
  if (subcategoryId === null || subcategoryId === undefined || subcategoryId === "") return list;
  if (subcategoryId === NO_SUBCATEGORY) {
    return list.filter((count) => count.counts_subcategory_id === undefined || count.counts_subcategory_id === null);
  }
  return list.filter((count) => sameId(count.counts_subcategory_id, subcategoryId));
}

// Запасной список статей `NewCount` для филиала без строк `counts_categories`:
// 31 значение, как было захардкожено, но уже с code/kind/operation_type, чтобы
// фильтр классов и тип платежа работали одинаково.
const FALLBACK_VALUES = [
  "Ремонт",
  "Обслуживание",
  "Запчасти",
  "Страховка",
  "Накладные расходы",
  "Транспортные расходы",
  "Налоги",
  "Штрафы",
  "Аренда имущества",
  "Мойка автомобилей",
  "Топливо",
  "Реклама",
  "Продажа имущества",
  "Покупка имущества",
  "Прочие расходы",
  "Прочие доходы",
  "Оплата аренды",
  "Доплата за доставку",
  "Доплата за приём",
  "Доплата за топливо",
  "Доплата за мойку",
  "Доплата за оборудование",
  "Доплата за дополнительное время",
  "Доплата за повреждения",
  "Доплата за штрафы",
  "Доплата за другое",
  "Доплата за превышение пробега",
  "Доплата за страховку",
  "Доплата за доп водителей",
  "Зарплата",
  "Выплаты партнёрам",
];

// Собирается один раз и заморожен: потребители только читают (фильтруют, ищут, копируют
// в новые массивы), так что каждый вызов отдаёт тот же массив.
const FALLBACK_CATEGORIES = Object.freeze(
  FALLBACK_VALUES.map((value) => {
    const code = CATEGORY_CODES[value];
    return Object.freeze({
      value,
      code,
      kind: kindByCode(code),
      operation_type: operationTypeByCode(code),
      active: true,
      subcategories: Object.freeze([]),
    });
  })
);

export function fallbackCategories() {
  return FALLBACK_CATEGORIES;
}

// ---------------------------------------------------------------------------
// FT-192 STEP-13: вкладка настроек «Статьи платежей».
// ---------------------------------------------------------------------------

// Классы статей, которые показывает вкладка настроек: service, base, regular.
// system пишет только сервер, mode — режимы формы (залог, переводы): ими настройки не управляют.
const SETTINGS_KINDS = ["service", "base", "regular"];
// Подкатегории заводятся у base и regular; service («Выплаты агентам») подкатегорий не имеет.
const SUBCATEGORY_KINDS = ["base", "regular"];

// Статьи для вкладки настроек по группам: { incomes, expenses } в порядке сервера.
// Доход/расход — по operation_type (isExpenseCategory); статья компании без класса
// и статья без определимого типа во вкладку не попадают.
export function settingsCategoryGroups(categories) {
  const list = Array.isArray(categories) ? categories : [];
  const incomes = [];
  const expenses = [];
  list.forEach((category) => {
    if (!category || !SETTINGS_KINDS.includes(categoryKind(category))) return;
    const expense = isExpenseCategory(category);
    if (expense === true) expenses.push(category);
    else if (expense === false) incomes.push(category);
  });
  return { incomes, expenses };
}

// Можно ли заводить подкатегории у статьи.
export function hasSubcategoryTree(category) {
  return SUBCATEGORY_KINDS.includes(categoryKind(category)) && !!categoryCode(category);
}

// «Оплата аренды» (base) скрыть нельзя: на ней держатся бронирования и отчёты.
export function canHideCategory(category) {
  return categoryKind(category) !== "base";
}

// Тело 422 при дубле: { existing_id, existing_active } — на верхнем уровне либо в errors.
export function duplicateInfo(error) {
  const data = error && error.response && error.response.data;
  if (!data || typeof data !== "object") return null;
  const source = data.existing_id !== undefined ? data : data.errors && typeof data.errors === "object" ? data.errors : {};
  const id = Array.isArray(source.existing_id) ? source.existing_id[0] : source.existing_id;
  if (id === undefined || id === null) return null;
  const active = Array.isArray(source.existing_active) ? source.existing_active[0] : source.existing_active;
  return { id, active };
}


// ---------------------------------------------------------------------------
// FT-192 task-24: сводка сумм по статьям в карточке автомобиля и у инвестора.
// ---------------------------------------------------------------------------

// Статьи каталога, которые сводки показывали всегда (и с нулём), — в прежнем порядке.
// Это весь каталог сида на момент, когда списки писались, без system и service
// («Сервисные сборы», «Пополнение баланса клиента», «Аренда с выкупом», «Выплаты агентам»):
// эти сводки их не показывали и не показывают.
export const CAR_SUMMARY_CODES = Object.freeze([
  "repair_expences",
  "service_expences",
  "parts_expences",
  "insurance_expences",
  "common_expences",
  "transport_expences",
  "tax_expences",
  "fines_expences",
  "office_expences",
  "clean_expences",
  "gas",
  "ads_expences",
  "sell_property",
  "buy_property",
  "other_expense",
  "different_income",
  "rental_cost",
  "delivery_payments",
  "checkout_payments",
  "gas_payments",
  "clean_payments",
  "equipment_payments",
  "add_hours_payments",
  "repair_payments",
  "fines_payments",
  "other_payments",
  "add_mileage_payments",
  "insurance_payments",
  "drivers_payments",
  "internal_transfers",
  "deposit",
  "salary",
  "partners_expences",
]);

// У инвестора тот же набор, но первыми — аренда, выплаты партнёрам и зарплата.
export const INVESTOR_SUMMARY_CODES = Object.freeze([
  "rental_cost",
  "partners_expences",
  "salary",
  "repair_expences",
  "service_expences",
  "parts_expences",
  "insurance_expences",
  "common_expences",
  "transport_expences",
  "tax_expences",
  "fines_expences",
  "office_expences",
  "clean_expences",
  "gas",
  "ads_expences",
  "sell_property",
  "buy_property",
  "other_expense",
  "different_income",
  "delivery_payments",
  "checkout_payments",
  "gas_payments",
  "clean_payments",
  "equipment_payments",
  "add_hours_payments",
  "repair_payments",
  "fines_payments",
  "other_payments",
  "add_mileage_payments",
  "insurance_payments",
  "drivers_payments",
  "internal_transfers",
  "deposit",
]);

// Код → название из сида (Count.group, по-русски).
const CODE_NAMES = Object.keys(CATEGORY_CODES).reduce((names, value) => {
  names[CATEGORY_CODES[value]] = value;
  return names;
}, {});

// Значения Count.group строк сводки по порядку:
//   1) `codes` — статьи каталога в заданном порядке, всегда (и с нулём). Платёж
//      сопоставляется со статьёй по строке group (ASM-01), поэтому значение — название
//      из сида, а не из дерева;
//   2) статьи компании без кода из дерева `categories`, у которых есть платежи
//      (`counts`), — в порядке дерева;
//   3) значения group платежей без кода, которых в дереве нет (статью сняли с
//      филиала, платёж другого филиала), — в порядке появления.
// Статьи каталога вне `codes` не добавляются, даже если по ним есть платежи, —
// для них сводка прежняя. Пустой group не показывается.
export function summaryCategoryValues(codes, categories, counts) {
  const values = (Array.isArray(codes) ? codes : []).map((code) => CODE_NAMES[code]).filter(Boolean);
  const seen = new Set(values);
  const used = new Set();
  (Array.isArray(counts) ? counts : []).forEach((count) => {
    const group = count && count.group;
    if (typeof group === "string" && group !== "" && !CATEGORY_CODES[group]) used.add(group);
  });
  const add = (value) => {
    if (seen.has(value)) return;
    seen.add(value);
    values.push(value);
  };
  (Array.isArray(categories) ? categories : []).forEach((category) => {
    if (category && categoryCode(category) === null && used.has(category.value)) add(category.value);
  });
  used.forEach(add);
  return values;
}
